// 261010 Epic 2 / P2b-2 — bước VIDEO của một video, CÓ thao tác (khung mới): tạo clip đầu / thêm bản clip, chọn bản, xoá bản, chỉnh tuỳ chọn gen (chỉ cho lượt kế).
// Viết mới (không trích `EventCard` 670 dòng của EventPlanTab): gọi thẳng `filmApi.genEvent / selectEventClip / deleteEventClip`, tái dùng `SkyEngineChips` (chỉ đọc) + helper thuần.
// Không có gen hàng loạt, edit / extend / trim clip, đổi engine, duyệt clip. KHÔNG hiện giá (User chốt). Quy tắc hiển thị / bật / xác nhận: `workflowActionPolicy` (thuần, có test).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Popconfirm, Select, Space, Spin, Tag, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent, GenTuning } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import { SkyEngineChips } from './SkyVideoPanel';
import { genModelOptionsFor } from './skyVideoMetaUtils';
import { generationLabelOf, optInTuningOf } from './workflowGenerationUtils';
import { clipFailInfo } from './clipErrorText';
import { clipGenInfoOf } from './workflowClipInfo'; // 261029 S4
import ClipPromptFold from './ClipPromptFold'; // 261029 S4 (dùng chung với màn chính)
import { truncateText } from './workflowBoardUtils';
import {
  actionOf, actionsFor, clipDeleteConfirmContent, genTuningPayload, videoConfirmContent, videoCtxOf, workflowActionError,
  type ActionError, type ActionState, type WorkflowActionCtx,
} from './workflowActionPolicy';
import { RunningTag, StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';
import { ClipRecommendationNote, ClipReviewRow, SelectReasonDialog } from './ClipReviewRow'; // 261010 P6
import { clipRowsView, selectGate } from './clipReviewPolicy';
import { reasonOutcomeText, selectWithReason } from './clipSelectFlow';
import { isRunActive } from './stepReviewPolicy';
import WorkflowGenerationEditor from './WorkflowGenerationEditor'; // 261010 UI2 (L2)
import WorkflowVideoL6Panel from './WorkflowVideoL6Panel'; // 261010 UI2 (L6)

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
  workflowManaged?:boolean;
}

const RESOLUTIONS = ['480p', '720p', '1080p'].map((r) => ({ label: r, value: r }));
const COUNTS = [{ label: '1 bản', value: 1 }, { label: '2 bản để so', value: 2 }, { label: '3 bản để so', value: 3 }];
const PRIME_REFRESH_MS = [2000, 6000]; // clip PENDING ghi vào DB trễ vài giây sau 202 ⇒ tải lại thêm hai lượt để thấy "đang tạo" không cần F5
const PENDING_POLL_MS = 10000; // clip PENDING mà không có job (vd. server khởi động lại giữa chừng) ⇒ trang không tự poll ⇒ bước tự tải lại

const WorkflowVideoStep: React.FC<Props> = ({ workflowManaged,storyId, eventId, ev, detail, jobs, onActionDone, onJobsRefresh, clipReviews }) => {
  const alive = useAliveRef();
  const [err, setErr] = useState<ActionError | null>(null);
  const [pendingSelect, setPendingSelect] = useState<{ clipId: string; label: string; reasons: string[]; reviewId: string | null } | null>(null); // 261010 P6: chọn bản chưa sạch ⇒ hỏi lý do (không chặn cứng)
  const [tuning, setTuning] = useState<GenTuning>({}); // lựa chọn cho LƯỢT GEN KẾ — giữ trong trạng thái bước, không lưu; bước được `key={eventId}` nên đổi video là về mặc định
  const [genKey, setGenKey] = useState<string | null>(null);
  const [clipBusy, setClipBusy] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  const video = useMemo(() => videoCtxOf(ev, tuning, jobs), [ev, tuning, jobs]);
  const ctx: WorkflowActionCtx = useMemo(() => ({ isSky: true, stepState: 'NOT_READY', runningJobKinds: [], video }), [video]);
  const actions = useMemo(() => actionsFor('VIDEO', ctx), [ctx]);
  const tuningAct = actionOf(actions, 'VIDEO_TUNING');
  const clips = ev.clips || [];
  const selected = clips.find((c) => c.id === ev.selectedClipId) || null;
  const clipStale = (ev.staleStages || []).includes('clip');
  const flowVersion = detail?.plan.flowVersion ?? 'sky';
  const modelOptions = genModelOptionsFor(flowVersion, ev.videoEngine);
  const savedGen = optInTuningOf(ev); // plan opt-in: model + độ phân giải ĐÃ LƯU là nguồn duy nhất của lượt gen (đổi ở ô Engine phía trên) — khối theo lượt chỉ còn số bản
  const selectedModel = ev.videoEngine === 'SEEDANCE_25' ? '2.5' : (tuning.model || 'mini');
  const pendingNoJob = clips.some((c) => c.status === 'PENDING') && !jobs.some((j) => j.eventId === eventId || (!j.eventId && j.key === eventId));
  // 261010 P6: dữ liệu từng dòng review + AI đề xuất (luật ở clipReviewPolicy.clipRowsView, có test). Không có `clipReviews` ⇒ bước chạy y như trước P6.
  const rv = useMemo(() => (clipReviews ? clipRowsView({
    clips: ev.clips || [], ev, resp: clipReviews.ctl.view, eventId, jobs, busy: clipReviews.ctl.busy !== null, busyClipId: clipReviews.ctl.busyClipId, runActive: isRunActive(jobs), focusClipId: clipReviews.focusClipId,
  }) : null), [ev, eventId, jobs, clipReviews]);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); timers.current = []; }, []);
  useEffect(() => {
    if (!pendingNoJob) return undefined;
    const t = window.setInterval(() => onActionDone(), PENDING_POLL_MS);
    return () => window.clearInterval(t);
  }, [pendingNoJob, onActionDone]);

  const doGen = async (a: ActionState) => {
    if(workflowManaged){message.info('Dùng Gen video này qua runner ở đầu bước.');return;}
    setErr(null); setGenKey(a.key);
    try {
      await filmApi.genEvent(eventId, !!a.payload?.variant, genTuningPayload(ev, tuning)); // cùng hàm với hộp xác nhận; resolution/model luôn tường minh (không rơi về env của BE)
      message.info('Đã gửi lệnh tạo clip — chạy ở nền, mất vài phút.');
      await onJobsRefresh(); // hỏi workflow-status NGAY để thấy job (và trang bắt đầu poll)
      PRIME_REFRESH_MS.forEach((ms) => { timers.current.push(window.setTimeout(() => { if (alive.current) onActionDone(); }, ms)); });
    } catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Tạo clip')); }
    finally { if (alive.current) setGenKey(null); onActionDone(); }
  };
  /**
   * Chọn bản. 261010 P6: BE không nhận `note` ở select-clip ⇒ lý do (nếu có) đi sau bằng decide ACCEPT lên phiếu của bản đó (clipSelectFlow, có test); lỗi ghi lý do không huỷ lựa chọn.
   * Không có lý do / reviewId (đường chọn thường) ⇒ chỉ `PUT select-clip {clipId}` + "Đã chọn bản clip", y như trước P6.
   */
  const finishSelect = async (clipId: string, note: string | null, reviewId: string | null) => {
    setErr(null); setClipBusy(`select:${clipId}`);
    const r = await selectWithReason(
      { select: (id) => filmApi.selectEventClip(eventId, id), decide: (rid, n) => filmApi.decideStepReview(rid, { decision: 'ACCEPT', note: n }) },
      { clipId, note, reviewId },
    );
    if (!r.selected) { if (alive.current) setErr(workflowActionError(r.selectError, 'Chọn clip')); }
    else { const t = reasonOutcomeText(r.reason, r.reasonError?.text); message[t.tone](t.text); }
    if (alive.current) setClipBusy(null);
    void clipReviews?.ctl.load(); // tag "Chọn tạm / Người đã chọn" + trạng thái phân xử của phiếu lấy từ clipBoards ⇒ nạp lại
    onActionDone();
  };
  const doSelect = (clipId: string) => finishSelect(clipId, null, null);
  /** 261010 P6: bản có lỗi nặng / NEEDS_LEADER chưa phân xử hoặc chưa chấm (khi video đã có phiếu clip) ⇒ hộp ghi lý do rồi mới chọn; còn lại chọn ngay như cũ. */
  const requestSelect = (clipId: string) => {
    const gate = clipReviews ? selectGate({ resp: clipReviews.ctl.view, eventId, targetClipId: clipId }) : null;
    if (gate?.required) { setPendingSelect({ clipId, label: rv?.rows[clipId]?.label ?? 'bản này', reasons: gate.reasons, reviewId: gate.reviewId }); return; }
    void doSelect(clipId);
  };
  const confirmSelect = async (note: string) => {
    if (!pendingSelect) return;
    await finishSelect(pendingSelect.clipId, note, pendingSelect.reviewId);
    if (alive.current) setPendingSelect(null);
  };
  const doDelete = async (clipId: string) => {
    setErr(null); setClipBusy(`delete:${clipId}`);
    try { await filmApi.deleteEventClip(clipId); message.success('Đã xoá bản clip'); }
    catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Xoá clip')); }
    finally { if (alive.current) setClipBusy(null); onActionDone(); }
  };

  const genButton = (a: ActionState | undefined, primary: boolean) => {
    if (!a || !a.visible) return null;
    const c = videoConfirmContent(a.key, video);
    return (
      <Popconfirm key={a.key} title={c.title} description={<div style={{ maxWidth: 320 }}>{c.body}</div>} okText={c.okText} cancelText={c.cancelText}
        disabled={!a.enabled} onConfirm={() => doGen(a)}>
        <Button size="small" type={primary ? 'primary' : 'default'} loading={genKey === a.key} disabled={!a.enabled} title={a.enabled ? undefined : a.reason} data-testid={`wf-${a.key.toLowerCase().replace(/_/g, '-')}`}>{a.label}</Button>
      </Popconfirm>
    );
  };
  const blocked = [actionOf(actions, 'VIDEO_GEN'), actionOf(actions, 'VIDEO_VARIANT')].find((a) => a?.visible && !a.enabled);

  return (
    <div data-testid="wf-video-step">
      <StepErrorAlert error={err} onReload={onActionDone} onClose={() => setErr(null)} />
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        <SkyEngineChips ev={ev} flowVersion={flowVersion} />
        {clipStale && <Tag color="orange" data-testid="wf-clip-stale">clip đã cũ — prompt hoặc nội dung đã đổi sau lần tạo</Tag>}
        {video.busy && <RunningTag>đang tạo clip ở nền…</RunningTag>}
      </Space>

      <WorkflowGenerationEditor eventId={eventId} eventOrder={ev.eventOrder} ev={ev} jobs={jobs} onActionDone={onActionDone} />
      <WorkflowVideoL6Panel storyId={storyId} eventId={eventId} eventOrder={ev.eventOrder} ev={ev} onActionDone={onActionDone} />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ width: 180, flex: '0 0 180px' }}>
          {selected?.videoUrl
            ? <video src={selected.videoUrl} controls preload="metadata" style={{ width: 180, aspectRatio: '9 / 16', borderRadius: 10, background: '#000', objectFit: 'contain' }} data-testid="wf-video-player" />
            : <div className="muted" style={{ width: 180, aspectRatio: '9 / 16', borderRadius: 10, border: '1px dashed var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 8 }} data-testid="wf-video-empty">Chưa có clip</div>}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{selected ? `Bản đang chọn: ${selected.variantLabel || 'v1'}${selected.durationS ? ` · ${selected.durationS}s` : ''}` : 'Chưa chọn bản nào'}</div>
          {selected ? <div className="muted mono" style={{ fontSize: 12 }} data-testid="wf-selected-gen">{clipGenInfoOf(selected, ev).line}</div> : null}
        </div>

        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{savedGen ? 'Cấu hình đã lưu cho video này' : 'Tuỳ chọn cho lượt tạo kế tiếp (không lưu)'}</div>
          <Space wrap size={8} style={{ marginBottom: 10 }}>
            {savedGen ? (
              <span className="mono" data-testid="wf-saved-gen" title="Đổi ở ô Engine phía trên rồi bấm Lưu engine">{generationLabelOf(ev.generation)}</span>
            ) : (
              <>
                <Select size="small" style={{ width: 200 }} value={selectedModel} options={modelOptions as any} disabled={!tuningAct?.enabled}
                  onChange={(m) => setTuning((t) => ({ ...t, model: m }))} aria-label="Model" />
                <Select size="small" style={{ width: 90 }} value={ev.videoEngine === 'SEEDANCE_25' ? '480p' : (tuning.resolution || '480p')} options={RESOLUTIONS} disabled={!tuningAct?.enabled || ev.videoEngine === 'SEEDANCE_25'}
                  onChange={(r) => setTuning((t) => ({ ...t, resolution: r }))} aria-label="Độ phân giải" />
              </>
            )}
            <Select size="small" style={{ width: 120 }} value={tuning.count || 1} options={COUNTS} disabled={!tuningAct?.enabled}
              onChange={(n) => setTuning((t) => ({ ...t, count: n }))} aria-label="Số bản" />
          </Space>
          <div style={{ marginBottom: 6 }}>
            <Space wrap size={8}>
              {genButton(actionOf(actions, 'VIDEO_GEN'), true)}
              {genButton(actionOf(actions, 'VIDEO_VARIANT'), false)}
            </Space>
          </div>
          {blocked && <div className="muted" style={{ fontSize: 12 }} data-testid="wf-video-blocked">{blocked.reason}</div>}
        </div>
      </div>

      <h3 style={{ marginTop: 14 }}>Các bản clip ({clips.length})</h3>
      {rv ? <ClipRecommendationNote line={rv.recommendation.line} /> : null}
      {clips.length === 0 ? <p className="muted">Chưa có clip nào.</p> : (
        <div className="wf-clips" data-testid="wf-clip-list">
          {clips.map((c) => {
            const sel = actionOf(actions, 'VIDEO_SELECT', c.id);
            const del = actionOf(actions, 'VIDEO_DELETE', c.id);
            const isSel = c.id === ev.selectedClipId;
            const fail = clipFailInfo(c);
            const gen = clipGenInfoOf(c, ev);   // 261029 S4: bản này đã gen bằng gì (đọc clip.meta, không suy từ cấu hình hiện tại)
            return (
              <div key={c.id} data-testid="wf-clip" style={{ border: isSel ? '2px solid var(--ok)' : '1px solid var(--line)', borderRadius: 10, padding: 8 }}>
                <div className="wf-kv"><b>{c.variantLabel || 'v1'}{isSel ? ' · đang chọn' : ''}</b>{c.durationS ? <span className="mono">{c.durationS}s</span> : null}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }} data-testid="wf-clip-gen">
                  <span className="mono">{gen.line}</span>
                  {gen.mismatch ? <Tag color="orange" style={{ marginLeft: 6 }} data-testid="wf-clip-gen-mismatch">{gen.mismatch}</Tag> : null}
                </div>
                {c.status === 'DONE' && c.videoUrl ? <video src={c.videoUrl} controls preload="none" style={{ width: '100%', maxHeight: 320, borderRadius: 8, background: '#000' }} /> : null}
                {c.status === 'PENDING' && <div style={{ padding: 12, textAlign: 'center' }}><Spin size="small" /> <span className="muted">đang tạo…</span></div>}
                {c.status === 'FAILED' && (
                  <p className="wf-alert ERROR" style={{ marginBottom: 6 }}>
                    <b>{fail?.label ?? 'Tạo clip lỗi'}</b>{c.error ? <><br />{truncateText(c.error, 300)}</> : null}
                  </p>
                )}
                {clipReviews && rv?.rows[c.id] ? (
                  <ClipReviewRow row={rv.rows[c.id]} onRun={() => { clipReviews.onFocusClip(c.id); void clipReviews.ctl.run('CLIP_REVIEW', c.id); }} onFocus={() => clipReviews.onFocusClip(c.id)} />
                ) : null}
                <Space wrap size={6} style={{ marginTop: 6 }}>
                  {sel?.visible && (
                    <Button size="small" type={isSel ? 'primary' : 'default'} loading={clipBusy === `select:${c.id}`} disabled={!sel.enabled} title={sel.enabled ? undefined : sel.reason}
                      onClick={() => requestSelect(c.id)} data-testid="wf-clip-select">{isSel ? '✓ Đang chọn' : 'Chọn bản này'}</Button>
                  )}
                  {del?.visible && (() => {
                    const dc = clipDeleteConfirmContent(isSel);
                    return (
                      <Popconfirm title={dc.title} description={<div style={{ maxWidth: 300 }}>{dc.body}</div>} okText={dc.okText} cancelText={dc.cancelText}
                        okButtonProps={{ danger: true }} disabled={!del.enabled} onConfirm={() => doDelete(c.id)}>
                        <Button size="small" danger loading={clipBusy === `delete:${c.id}`} disabled={!del.enabled} title={del.enabled ? undefined : del.reason} data-testid="wf-clip-delete">{del.label}</Button>
                      </Popconfirm>
                    );
                  })()}
                </Space>
                {sel?.visible && !sel.enabled && !isSel && <div className="muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="wf-clip-select-reason">{sel.reason}</div>}
                {c.prompt ? <ClipPromptFold prompt={c.prompt} /> : null}
              </div>
            );
          })}
        </div>
      )}
      <p className="wf-note" style={{ marginTop: 10 }}>"Có kết quả" ở bước này nghĩa là đã chọn một bản clip xong. Hệ thống chưa lưu việc người duyệt clip nên bước này không có "đã duyệt".</p>
      {pendingSelect ? <SelectReasonDialog label={pendingSelect.label} reasons={pendingSelect.reasons} storable={!!pendingSelect.reviewId} busy={clipBusy === `select:${pendingSelect.clipId}`} onConfirm={(note) => { void confirmSelect(note); }} onCancel={() => setPendingSelect(null)} /> : null}
    </div>
  );
};

export default WorkflowVideoStep;
